<script setup lang="ts">
// Contract 29 §10 — the ONE legal modal, replacing three hardcoded-prose modals.
//
// It renders no legal text of its own. Today every slug resolves to either an external public page
// (the caller links out and never mounts this) or `unavailable`, which is what this component
// exists to say honestly. §14.14: never a blank modal on a consent screen.
import { computed } from 'vue'

import { useLegalDocument } from '../../composables/useLegalDocument'
import { LEGAL_HREFS, type LegalDocumentSlug } from '../../models/legal.types'

import { useI18n } from '@/shared/i18n'
import { Alert } from '@/shared/ui'
import { Modal } from '@/shared/ui/modal'

const props = defineProps<{ modelValue: boolean; slug: LegalDocumentSlug }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const { t } = useI18n()
const legal = useLegalDocument(props.slug)

/** i18n keys are camelCase; the slugs are the wire's kebab-case (§13.1). */
const MESSAGE_SEGMENT: Record<LegalDocumentSlug, string> = {
  'user-agreement': 'userAgreement',
  'terms-of-service': 'termsOfService',
  'privacy-policy': 'privacyPolicy',
}

const title = computed(() => t(`globalSurface.legal.${MESSAGE_SEGMENT[props.slug]}`))
/** The public pages we CAN point at, so an unavailable document is still a helpful dead end. */
const published = computed(() =>
  (Object.keys(LEGAL_HREFS) as LegalDocumentSlug[])
    .filter((slug) => LEGAL_HREFS[slug])
    .map((slug) => ({
      slug,
      href: LEGAL_HREFS[slug] as string,
      label: t(`globalSurface.legal.${MESSAGE_SEGMENT[slug]}`),
    })),
)
</script>

<template>
  <Modal
    :model-value="props.modelValue"
    :title="title"
    size="lg"
    :close-label="t('common.close')"
    data-testid="legal-document-modal"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <Alert
      v-if="legal.mode.value === 'unavailable'"
      tone="info"
      :title="t('globalSurface.legal.unavailable')"
      data-testid="legal-document-unavailable"
    >
      <p class="legal-modal__body mt-0 mx-0 mb-xs">
        {{ t('globalSurface.legal.unavailableBody') }}
      </p>
      <ul class="legal-modal__links grid gap-2xs m-0 ps-md">
        <li v-for="entry in published" :key="entry.slug">
          <a :href="entry.href" target="_blank" rel="noopener noreferrer">
            {{ entry.label }}
            <span class="legal-modal__hint ms-2xs text-muted text-[0.75rem]">{{
              t('globalSurface.legal.openExternal')
            }}</span>
          </a>
        </li>
      </ul>
    </Alert>
  </Modal>
</template>
